import { useTranslation } from 'react-i18next';
import posthog from 'posthog-js';
import type { MapObject } from '../../../../models/mapObject';
import { CHAT_URL } from '../../../../config';
import useMapModalStore from '../../../../stores/useMapModalStore';
import useMapOnboardingStore from '../../../../stores/useMapOnboardingStore';
import useMapViewStore from '../../../../stores/useMapViewStore';
import ModalFrame from '../../../../ui/primitives/ModalFrame';

interface BuildHousePopupHostProps {
  /** react-query plots list — intentionally stays a prop. */
  plots: MapObject[];
}

/** Onboarding popup: "Build your first house". JSX moved verbatim. */
export default function BuildHousePopupHost({ plots }: BuildHousePopupHostProps) {
  const { t } = useTranslation();
  const openModal = useMapModalStore((s) => s.openModal);
  const isOnboardingBuildHousePopupOpen = useMapOnboardingStore((s) => s.isOnboardingBuildHousePopupOpen);
  const setIsOnboardingBuildHousePopupOpen = useMapOnboardingStore((s) => s.setIsOnboardingBuildHousePopupOpen);
  const onboardingNoPlotsMessage = useMapOnboardingStore((s) => s.onboardingNoPlotsMessage);
  const setOnboardingNoPlotsMessage = useMapOnboardingStore((s) => s.setOnboardingNoPlotsMessage);
  const setIsOnboardingZooming = useMapOnboardingStore((s) => s.setIsOnboardingZooming);
  const setShouldZoomToPlot = useMapViewStore((s) => s.setShouldZoomToPlot);

  return (
        <ModalFrame
          isOpen={isOnboardingBuildHousePopupOpen}
          onClose={() => {}}
          closeOnBackdropClick={false}
          variant="dialog"
          className="max-w-[400px] rounded-xl border-[3px] border-deep-blue"
          renderHeader={() => (
            <div
              style={{
                padding: '20px 20px 16px 20px',
                textAlign: 'center',
                fontFamily: 'Rubik One, sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                color: '#fff',
                lineHeight: '1.3',
              }}
            >
              {t("Real Estate")}
            </div>
          )}
        >
          {onboardingNoPlotsMessage ? (
            <>
              <button
                onClick={() => {
                  // No free plots to claim → still finish onboarding so the
                  // player isn't soft-locked (blocked bottom nav + elder dialog
                  // re-shown on every relaunch). The reward modal auto-claims
                  // and sets onboarding_completed.
                  setIsOnboardingBuildHousePopupOpen(false);
                  setOnboardingNoPlotsMessage(false);
                  openModal('tutorialReward');
                  posthog.capture('onboarding_completed_no_free_plots');
                }}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  background: '#37BFA4',
                  border: 'none',
                  borderRadius: 100,
                  color: '#fff',
                  fontFamily: 'Rubik One, sans-serif',
                  fontSize: 16,
                  fontWeight: 400,
                  cursor: 'pointer',
                  marginBottom: 24,
                }}
              >
                {t("OK")}
              </button>
              <div style={{ fontFamily: 'Lato, sans-serif', fontSize: 15, fontWeight: 300, lineHeight: '1.4', color: '#fff', textAlign: 'center' }}>
                {t("Sorry, there are no free plots on the map right now. Free plots appear on the map from time to time. You can also buy a house from another player for TON/USDT.")}
              </div>
              <div style={{ fontFamily: 'Lato, sans-serif', fontSize: 15, fontWeight: 300, lineHeight: '1.4', color: '#fff', textAlign: 'center', marginTop: 12 }}>
                {t("You can also join our chat — announcements about newly freed plots are posted there regularly!")}
              </div>
              <button
                onClick={() => {
                  window.open(CHAT_URL, '_blank');
                }}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  background: 'transparent',
                  border: '1px solid #37BFA4',
                  borderRadius: 100,
                  color: '#37BFA4',
                  fontFamily: 'Rubik One, sans-serif',
                  fontSize: 16,
                  fontWeight: 400,
                  cursor: 'pointer',
                  marginTop: 12,
                  marginBottom: 0,
                }}
              >
                {t("Join chat")}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  if (plots.length === 0) {
                    posthog.capture('onboarding_no_free_plots');
                    setOnboardingNoPlotsMessage(true);
                    return;
                  }
                  posthog.capture('onboarding_search_free_plot_clicked');
                  setIsOnboardingBuildHousePopupOpen(false);
                  setIsOnboardingZooming(true);
                  setShouldZoomToPlot(true);
                }}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  background: '#37BFA4',
                  border: 'none',
                  borderRadius: 100,
                  color: '#fff',
                  fontFamily: 'Rubik One, sans-serif',
                  fontSize: 16,
                  fontWeight: 400,
                  cursor: 'pointer',
                  marginBottom: 24,
                }}
              >
                {t("Search for a free plot")}
              </button>
              <div style={{ fontFamily: 'Lato, sans-serif', fontSize: 15, fontWeight: 300, lineHeight: '1.4', color: '#fff', textAlign: 'center' }}>
                {t("Now it's time to build your first house so that all island residents can see you on the map and come visit!")}
              </div>
            </>
          )}
        </ModalFrame>
  );
}
